import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { useBeneficiariesActions } from '@/entities/beneficiary';
import { useFileEncryptionUpload } from '@/entities/file/hooks/use-file-encryption-upload';
import { useIwEditingStore } from '@/entities/interwills';
import { useIwRecordingStore } from '@/entities/interwills/stores/iwRecording.store';
import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api';
import { useGetBeneficiariesLazyQuery } from '@/shared/generated/graphql';
import { getPersonFullName } from '@/shared/helpers/names';
import { useProfileStore } from '@/shared/store/profile.store';
import { files } from '@/shared/utils/files';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { RecordingModalProps } from '@/widgets/modals/types';

import { ModalContainerV3 } from '../containers/ModalContainerV3';

import { SavedPreviewTab } from './components/SavedPreviewTab';
import { PreviewTab, VideoTab } from './components';

export enum RecordingModalTabs {
  RECORD,
  PREVIEW,
  SAVED_PREVIEW,
}

export type RecordingVideo = {
  url: string;
  blob: Blob;
  duration: number | null;
  resolution: string | null;
  frameRate: number | null;
  startedAt: string | null;
};

export const RecordingModal: FC<RecordingModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'recordingModal' });

  const [tab, setTab] = useState(RecordingModalTabs.RECORD);
  const [video, setVideo] = useState<RecordingVideo | null>(null);

  const {
    recordingFor,
    witnesses,
    witnessesDecoded,
    savedPreview,
    setTestator,
    setWintesses,
    setTestatorDecoded,
    setWitnessesDecoded,
    setSavedPreview,
  } = useIwRecordingStore();
  const { witnesses: witnessesPersonData } = useIwEditingStore();
  const modals = useModalsStore();
  const notices = useNotices();
  const { encryptAndUploadFile } = useFileEncryptionUpload({
    withWorker: true,
    useDirectUrl: true,
  });
  const { isBeneficiary } = useProfileStore();
  const api = useApi();
  // TODO: replace with REST
  const [getBeneficiaries] = useGetBeneficiariesLazyQuery();
  const beneficiariesActions = useBeneficiariesActions();

  const saveVideo = async () => {
    if (!video) return;
    let filename = `testator_video.mp4`;
    if (recordingFor?.witnessId) {
      const person = witnessesPersonData.find((w) => w.id === recordingFor.witnessId);
      filename = `witness_video (${
        person ? getPersonFullName(person) : recordingFor?.witnessId
      }).mp4`;
    }
    const file = new File([video.blob], filename);

    let additionalPublicKey: string | null = null;
    if (isBeneficiary) {
      const res = await api.profilesGet();
      additionalPublicKey = res.data?.mainUser?.publicKey ?? null;
    } else if (recordingFor?.witnessId) {
      const currentBeneficiary = beneficiariesActions.findById(recordingFor.witnessId);
      const allBeneficiaries = await getBeneficiaries();
      const centralDbBeneficiary = allBeneficiaries.data?.getBeneficiaries.find(
        (item) => item.id === currentBeneficiary?.centralDbProfileId,
      );
      additionalPublicKey = centralDbBeneficiary?.publicKey ?? null;
    }

    const filePath = await encryptAndUploadFile(file, {
      publicKey: additionalPublicKey,
      itemData: { isFileAttached: true },
    });

    const obj = {
      startedAt: video.startedAt,
      endedAt: new Date().toISOString(),
      duration: video.duration ?? 0,
      url: filePath,
      frameRate: video.frameRate,
      resolution: video.resolution,
    };

    if (!filePath) {
      notices.error('File upload failed');
      return;
    }

    if (!recordingFor?.witnessId) {
      setTestator(obj);
      setTestatorDecoded(video.blob);
    } else {
      const newWitnesses = {
        ...witnesses,
        [recordingFor.witnessId]: obj,
      };
      const newWitnessesDecoded = { ...witnessesDecoded, [recordingFor.witnessId]: video.blob };
      setWitnessesDecoded(newWitnessesDecoded);
      setWintesses(newWitnesses);
    }

    modals.closeModal();
    modals.openModal({
      type: 'VideoSavedModal',
      closeRecording: () => {
        modals.closeModal();
      },
    });
  };

  useEffect(() => {
    setTab(RecordingModalTabs.RECORD);
    setVideo(null);
    setSavedPreview(null);
  }, [recordingFor]);

  useEffect(() => {
    if (!savedPreview) return;
    setVideo({
      blob: savedPreview,
      url: files.blobToUrl(savedPreview),
      frameRate: null,
      resolution: null,
      duration: null,
      startedAt: null,
    });
    setTab(RecordingModalTabs.SAVED_PREVIEW);
  }, [savedPreview]);

  return (
    <ModalContainerV3
      title={t('title', { role: recordingFor?.role, fullname: recordingFor?.name })}
      showBackBtn={tab !== RecordingModalTabs.PREVIEW}
    >
      {(tab === RecordingModalTabs.RECORD || !video?.url) && (
        <VideoTab
          roleName={recordingFor?.role ?? 'Testator'}
          witnessId={recordingFor?.witnessId ?? undefined}
          setVideo={(videoObj) => {
            setVideo(videoObj);
            setTab(RecordingModalTabs.PREVIEW);
          }}
        />
      )}

      {tab === RecordingModalTabs.PREVIEW && video?.url && (
        <PreviewTab
          onBack={() => {
            setVideo(null);
            setTab(RecordingModalTabs.RECORD);
          }}
          onSave={saveVideo}
          videoUrl={video?.url}
        />
      )}

      {tab === RecordingModalTabs.SAVED_PREVIEW && video?.url && (
        <SavedPreviewTab videoUrl={video?.url} setTab={setTab} />
      )}
    </ModalContainerV3>
  );
};
